import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { BarChart3 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { humanizeColumnName } from '../../lib/formatters';
import { COLORS } from '../../lib/analysisUtils';
import { DemographicFilter } from '../AnalysisEnhancements';
import DemographicBreakdownAccordion from './DemographicBreakdownAccordion';
import CrossDemographicsMatrix from '../CrossDemographicsMatrix';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function BreakdownTab({
  barData, breakdown, orderedResponseScale, scaleColors, scaleColorMap,
  groupBy, setGroupBy, population,
  filterField, setFilterField, filterValue, setFilterValue,
  allBreakdowns, aggregation,
  crossDemographic1, setCrossDemographic1,
  crossDemographic2, setCrossDemographic2,
  crossAnalysisData, setCrossAnalysisData,
  crossAnalysisLoading,
  availableDemographics, stimulus,
}) {
  // Validate: segment percentages should sum to ~100% for each row
  useMemo(() => {
    if (!barData?.length || !orderedResponseScale?.length) return;
    for (const row of barData) {
      const sum = orderedResponseScale.reduce((s, r) => s + (row[r] || 0), 0);
      if (Math.abs(sum - 100) > 1.5) {
        console.error(`[BreakdownTab] Segment sum for "${row.name}" = ${sum.toFixed(1)}%, expected ~100%`);
      }
    }
  }, [barData, orderedResponseScale]);
  return (
    <div className="space-y-6">
      <Card className="border border-border chart-animate">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-lg">Demographic Breakdown</CardTitle>
            <div className="flex items-center gap-3">
              <DemographicFilter
                population={population}
                filterField={filterField}
                filterValue={filterValue}
                onFilterFieldChange={(v) => { setFilterField(v); setFilterValue(''); }}
                onFilterValueChange={setFilterValue}
                onClear={() => { setFilterField(''); setFilterValue(''); }}
                compact={true}
              />
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="w-48" data-testid="group-by-select">
                  <SelectValue placeholder="Group by" />
                </SelectTrigger>
                <SelectContent>
                  {population?.columns?.filter(col => (col.unique_count || 0) <= 25).map(col => (
                    <SelectItem key={col.name} value={col.name}>{col.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {barData.length > 0 && breakdown ? (
            <>
              <ResponsiveContainer width="100%" height={Math.max(180, barData.length * 65)}>
                <BarChart data={barData} layout="vertical" barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${Math.round(v)}%`} tick={{ fill: 'currentColor' }} className="text-muted-foreground" />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12, fill: 'currentColor' }} className="text-muted-foreground" />
                  <Tooltip formatter={(value) => `${value.toFixed(1)}%`} contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '8px' }} labelStyle={{ color: '#fff' }} itemStyle={{ color: '#fff' }} />
                  {orderedResponseScale.length > 0
                    ? orderedResponseScale.map((response) => (
                        <Bar key={response} dataKey={response} stackId="a" fill={scaleColorMap?.[response] || scaleColors[orderedResponseScale.indexOf(response)] || '#94A3B8'} name={response}
                          label={({ x, y, width, height, index }) => {
                            const segmentPct = barData[index]?.[response] || 0;
                            if (width < 30 || segmentPct < 5) return null;
                            return <text x={x + width / 2} y={y + height / 2} fill="#fff" fontSize={10} fontWeight={600} textAnchor="middle" dominantBaseline="central">{Math.round(segmentPct)}%</text>;
                          }}
                        />
                      ))
                    : breakdown.response_scale?.map((response) => (
                        <Bar key={response} dataKey={response} stackId="a" fill={COLORS[orderedResponseScale.indexOf(response) % COLORS.length]} name={response}
                          label={({ x, y, width, height, index }) => {
                            const segmentPct = barData[index]?.[response] || 0;
                            if (width < 30 || segmentPct < 5) return null;
                            return <text x={x + width / 2} y={y + height / 2} fill="#fff" fontSize={10} fontWeight={600} textAnchor="middle" dominantBaseline="central">{Math.round(segmentPct)}%</text>;
                          }}
                        />
                      ))
                  }
                </BarChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-4 flex-wrap">
                {orderedResponseScale.map((response) => (
                  <div key={response} className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded" style={{ backgroundColor: scaleColorMap?.[response] || scaleColors[orderedResponseScale.indexOf(response)] || '#94A3B8' }} />
                    <span className="text-sm text-muted-foreground">{response}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">Select an attribute to see breakdown</div>
          )}
        </CardContent>
      </Card>

      {/* Accordion */}
      {allBreakdowns && Object.keys(allBreakdowns).length > 0 && (
        <DemographicBreakdownAccordion allBreakdowns={allBreakdowns} orderedResponseScale={orderedResponseScale} scaleColors={scaleColors} scaleColorMap={scaleColorMap} aggregation={aggregation} />
      )}

      {/* Cross-Demographics */}
      <Card className="border border-slate-200/80 dark:border-border">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg">Cross-Demographics Analysis</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Compare how two demographic attributes interact</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-3 pt-3 border-t border-slate-100 dark:border-border">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Primary:</span>
              <Select value={crossDemographic1} onValueChange={setCrossDemographic1}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Select attribute" /></SelectTrigger>
                <SelectContent>{availableDemographics.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <span className="text-slate-300">x</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-slate-500">Secondary:</span>
              <Select value={crossDemographic2} onValueChange={setCrossDemographic2} disabled={!crossDemographic1}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue placeholder="Select attribute" /></SelectTrigger>
                <SelectContent>{availableDemographics.filter(d => d !== crossDemographic1).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(crossDemographic1 || crossDemographic2) && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setCrossDemographic1(''); setCrossDemographic2(''); setCrossAnalysisData(null); }}>Clear</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {crossAnalysisLoading ? (
            <CrossDemographicsMatrix data={[]} responseScale={[]} demographic1={crossDemographic1} demographic2={crossDemographic2} onCellClick={() => {}} loading={true} />
          ) : crossAnalysisData && crossAnalysisData.is_cross_analysis && stimulus ? (
            <CrossDemographicsMatrix data={crossAnalysisData.breakdown} responseScale={stimulus.response_scale} demographic1={crossDemographic1} demographic2={crossDemographic2} onCellClick={() => {}} />
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground border border-dashed border-border rounded-lg bg-muted/30">
              <div className="text-center space-y-2">
                <BarChart3 className="w-8 h-8 mx-auto opacity-20" />
                <p className="text-sm font-medium">Select two attributes above</p>
                <p className="text-xs text-muted-foreground max-w-xs">Choose a primary and secondary demographic attribute to see how they interact.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
